// src/pages/LiveView.jsx
//
// Real-time god view: watch Claire work as it happens.
// Uses Supabase Realtime to stream new events into platform-specific panes
// as they arrive -- interactions (lead-tied), relationship_events (social
// contacts), content_drafts (what she's drafting/posting), and
// opportunities (what she's surfacing). No manual refresh needed.
//
// Layout: a grid of panes, one per platform/activity type. Each pane
// scrolls independently and pins to the bottom as new items arrive,
// so you always see the latest. Click any item to pause auto-scroll
// on that pane so you can read it.

import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";

const PANES = [
  { id: "twitter",       label: "Twitter",       color: "#0ea5e9", bg: "#f0f9ff" },
  { id: "instagram",     label: "Instagram",     color: "#ec4899", bg: "#fdf2f8" },
  { id: "discord",       label: "Discord",       color: "#7c3aed", bg: "#f5f3ff" },
  { id: "telegram",      label: "Telegram",      color: "#0284c7", bg: "#e0f2fe" },
  { id: "gmail",         label: "Email",         color: "#2563eb", bg: "#eff6ff" },
  { id: "opportunities", label: "Opportunities", color: "#16a34a", bg: "#f0fdf4" },
  { id: "drafts",        label: "Drafts",        color: "#d97706", bg: "#fffbeb" },
];

function timeAgo(ts) {
  if (!ts) return "";
  const diff = Math.floor((Date.now() - new Date(ts)) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return new Date(ts).toLocaleDateString();
}

function EventCard({ item, paneId }) {
  const isOutbound = item.direction === "outbound";
  return (
    <div style={{
      padding: "8px 10px",
      marginBottom: 6,
      borderRadius: 8,
      background: isOutbound ? "#fff" : "#fafafa",
      border: `1px solid ${isOutbound ? "#dbeafe" : "#e5e7eb"}`,
      borderLeft: `3px solid ${isOutbound ? "#3b82f6" : "#10b981"}`,
      fontSize: 12,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", color: "#6b7280", marginBottom: 3 }}>
        <span style={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
          {item.type || item.event_type || item.status || "event"}
        </span>
        <span>{timeAgo(item.occurred_at || item.created_at || item.inserted_at)}</span>
      </div>
      {item.handle && (
        <div style={{ color: "#374151", marginBottom: 2 }}>
          <span style={{ color: "#6b7280" }}>{isOutbound ? "→" : "←"} </span>
          <strong>{item.display_name || item.handle}</strong>
          {item.handle !== item.display_name && item.handle && (
            <span style={{ color: "#9ca3af" }}> @{item.handle}</span>
          )}
        </div>
      )}
      {item.lead_name && (
        <div style={{ color: "#374151", marginBottom: 2 }}>
          <span style={{ color: "#6b7280" }}>{isOutbound ? "→" : "←"} </span>
          <strong>{item.lead_name}</strong>
        </div>
      )}
      {(item.content || item.draft_text || item.title) && (
        <div style={{ color: "#1f2937", lineHeight: 1.4, marginTop: 3 }}>
          {(item.content || item.draft_text || item.title || "").slice(0, 200)}
          {(item.content || item.draft_text || item.title || "").length > 200 && "…"}
        </div>
      )}
      {item.context_content && (
        <div style={{ color: "#9ca3af", fontSize: 11, marginTop: 3, fontStyle: "italic" }}>
          ↳ "{item.context_content.slice(0, 100)}{item.context_content.length > 100 ? "…" : ""}"
        </div>
      )}
      {item.lead_id && (
        <Link to={`/leads/${item.lead_id}`} style={{ color: "#6366f1", fontSize: 11, marginTop: 3, display: "block" }}>
          View lead →
        </Link>
      )}
    </div>
  );
}

function OpportunityCard({ item }) {
  return (
    <div style={{
      padding: "8px 10px",
      marginBottom: 6,
      borderRadius: 8,
      background: "#fff",
      border: "1px solid #d1fae5",
      borderLeft: "3px solid #10b981",
      fontSize: 12,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", color: "#6b7280", marginBottom: 3 }}>
        <span style={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
          {item.category || "opportunity"}
        </span>
        <span>{timeAgo(item.created_at)}</span>
      </div>
      <div style={{ color: "#1f2937", fontWeight: 500, marginBottom: 3 }}>{item.title}</div>
      {item.summary && (
        <div style={{ color: "#374151", lineHeight: 1.4 }}>
          {item.summary.slice(0, 180)}{item.summary.length > 180 ? "…" : ""}
        </div>
      )}
      <div style={{ marginTop: 4, color: "#9ca3af", fontSize: 11 }}>Status: {item.status}</div>
    </div>
  );
}

function DraftCard({ item }) {
  return (
    <div style={{
      padding: "8px 10px",
      marginBottom: 6,
      borderRadius: 8,
      background: "#fff",
      border: "1px solid #fde68a",
      borderLeft: "3px solid #f59e0b",
      fontSize: 12,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", color: "#6b7280", marginBottom: 3 }}>
        <span style={{ fontWeight: 600, textTransform: "uppercase" }}>
          {item.platform} · {item.type || "draft"}
        </span>
        <span style={{
          padding: "1px 6px",
          borderRadius: 9999,
          background: item.status === "approved" ? "#d1fae5" : item.status === "posted" || item.status === "sent" ? "#dbeafe" : "#fef9c3",
          color: item.status === "approved" ? "#065f46" : item.status === "posted" || item.status === "sent" ? "#1e40af" : "#92400e",
          fontSize: 10,
          fontWeight: 600,
        }}>
          {item.status}
        </span>
      </div>
      {item.draft_text && (
        <div style={{ color: "#1f2937", lineHeight: 1.4 }}>
          {item.draft_text.slice(0, 200)}{item.draft_text.length > 200 ? "…" : ""}
        </div>
      )}
      <div style={{ marginTop: 3, color: "#9ca3af", fontSize: 11 }}>{timeAgo(item.created_at)}</div>
    </div>
  );
}

function Pane({ pane, items }) {
  const bottomRef = useRef(null);
  const [pinned, setPinned] = useState(true);

  useEffect(() => {
    if (pinned && bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [items, pinned]);

  return (
    <div style={{
      display: "flex",
      flexDirection: "column",
      background: pane.bg,
      border: `1px solid ${pane.color}33`,
      borderRadius: 10,
      overflow: "hidden",
      minHeight: 300,
      maxHeight: 500,
    }}>
      {/* Header */}
      <div style={{
        padding: "8px 12px",
        background: pane.color,
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexShrink: 0,
      }}>
        <span style={{ fontWeight: 700, fontSize: 13 }}>{pane.label}</span>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{
            background: "rgba(255,255,255,0.25)",
            borderRadius: 9999,
            padding: "1px 8px",
            fontSize: 11,
          }}>
            {items.length}
          </span>
          <button
            onClick={() => setPinned((v) => !v)}
            title={pinned ? "Auto-scrolling — click to pause" : "Paused — click to resume"}
            style={{
              background: pinned ? "rgba(255,255,255,0.3)" : "rgba(255,255,255,0.1)",
              border: "none",
              borderRadius: 4,
              color: "#fff",
              fontSize: 10,
              padding: "2px 6px",
              cursor: "pointer",
            }}
          >
            {pinned ? "⬇ live" : "⏸ paused"}
          </button>
        </div>
      </div>

      {/* Items */}
      <div
        style={{ overflowY: "auto", padding: 10, flex: 1 }}
        onScroll={(e) => {
          const el = e.currentTarget;
          const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
          setPinned(atBottom);
        }}
      >
        {items.length === 0 ? (
          <div style={{ color: "#9ca3af", fontSize: 12, textAlign: "center", paddingTop: 20 }}>
            Waiting for activity…
          </div>
        ) : (
          items.map((item) => {
            if (pane.id === "opportunities") return <OpportunityCard key={item.id} item={item} />;
            if (pane.id === "drafts") return <DraftCard key={item.id} item={item} />;
            return <EventCard key={item.id} item={item} paneId={pane.id} />;
          })
        )}
        <div ref={bottomRef} />
      </div>
    </div>
  );
}

export default function LiveView() {
  const [paneItems, setPaneItems] = useState(() =>
    Object.fromEntries(PANES.map((p) => [p.id, []]))
  );
  const [connected, setConnected] = useState(false);
  const [lastEvent, setLastEvent] = useState(null);

  const addItem = useCallback((paneId, item) => {
    setPaneItems((prev) => ({
      ...prev,
      [paneId]: [...prev[paneId], item].slice(-100), // keep last 100 per pane
    }));
    setLastEvent(new Date().toLocaleTimeString());
  }, []);

  // Load recent history for each pane on mount
  useEffect(() => {
    const since = new Date(Date.now() - 4 * 3600_000).toISOString(); // last 4 hours

    // Interactions (twitter, instagram, discord, telegram, gmail)
    supabase
      .from("interactions")
      .select("*, leads(id, name)")
      .gte("occurred_at", since)
      .order("occurred_at", { ascending: true })
      .limit(50)
      .then(({ data }) => {
        (data || []).forEach((row) => {
          const item = {
            ...row,
            lead_name: row.leads?.name,
            lead_id: row.leads?.id,
          };
          const paneId = PANES.find((p) => p.id === row.platform)?.id;
          if (paneId) {
            setPaneItems((prev) => ({
              ...prev,
              [paneId]: [...prev[paneId], item],
            }));
          }
        });
      });

    // Relationship events
    supabase
      .from("relationship_events")
      .select("*, relationships(platform, handle, display_name)")
      .gte("occurred_at", since)
      .order("occurred_at", { ascending: true })
      .limit(50)
      .then(({ data }) => {
        (data || []).forEach((row) => {
          const platform = row.relationships?.platform;
          const paneId = PANES.find((p) => p.id === platform)?.id;
          const item = {
            ...row,
            handle: row.relationships?.handle,
            display_name: row.relationships?.display_name,
            type: row.event_type,
          };
          if (paneId) {
            setPaneItems((prev) => ({
              ...prev,
              [paneId]: [...prev[paneId], item],
            }));
          }
        });
      });

    // Opportunities
    supabase
      .from("opportunities")
      .select("id, title, summary, category, status, created_at")
      .gte("created_at", since)
      .order("created_at", { ascending: true })
      .limit(30)
      .then(({ data }) => {
        (data || []).forEach((row) => {
          setPaneItems((prev) => ({
            ...prev,
            opportunities: [...prev.opportunities, row],
          }));
        });
      });

    // Drafts
    supabase
      .from("content_drafts")
      .select("id, platform, type, status, draft_text, created_at")
      .gte("created_at", since)
      .order("created_at", { ascending: true })
      .limit(30)
      .then(({ data }) => {
        (data || []).forEach((row) => {
          setPaneItems((prev) => ({
            ...prev,
            drafts: [...prev.drafts, row],
          }));
        });
      });
  }, []);

  // Realtime subscriptions
  useEffect(() => {
    const channel = supabase
      .channel("live-view")
      .on("postgres_changes", {
        event: "INSERT",
        schema: "public",
        table: "interactions",
      }, (payload) => {
        const row = payload.new;
        const paneId = PANES.find((p) => p.id === row.platform)?.id;
        if (paneId) addItem(paneId, row);
      })
      .on("postgres_changes", {
        event: "INSERT",
        schema: "public",
        table: "relationship_events",
      }, async (payload) => {
        const row = payload.new;
        // Fetch the parent relationship to get platform + handle
        const { data: rel } = await supabase
          .from("relationships")
          .select("platform, handle, display_name")
          .eq("id", row.relationship_id)
          .maybeSingle();
        const paneId = rel ? PANES.find((p) => p.id === rel.platform)?.id : null;
        if (paneId) {
          addItem(paneId, {
            ...row,
            handle: rel.handle,
            display_name: rel.display_name,
            type: row.event_type,
          });
        }
      })
      .on("postgres_changes", {
        event: "INSERT",
        schema: "public",
        table: "opportunities",
      }, (payload) => {
        addItem("opportunities", payload.new);
      })
      .on("postgres_changes", {
        event: "INSERT",
        schema: "public",
        table: "content_drafts",
      }, (payload) => {
        addItem("drafts", payload.new);
      })
      .on("postgres_changes", {
        event: "UPDATE",
        schema: "public",
        table: "content_drafts",
      }, (payload) => {
        // Show status changes (approved, posted, etc.) as new events in the drafts pane
        addItem("drafts", payload.new);
      })
      .subscribe((status) => {
        setConnected(status === "SUBSCRIBED");
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [addItem]);

  const totalItems = Object.values(paneItems).reduce((s, arr) => s + arr.length, 0);

  return (
    <div style={{ padding: 16, minHeight: "100vh", background: "#f9fafb" }}>
      {/* Header bar */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>Live View</h1>
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          padding: "4px 10px",
          borderRadius: 9999,
          background: connected ? "#dcfce7" : "#fee2e2",
          border: `1px solid ${connected ? "#86efac" : "#fca5a5"}`,
          fontSize: 12,
          fontWeight: 600,
          color: connected ? "#166534" : "#991b1b",
        }}>
          <span style={{
            width: 8, height: 8, borderRadius: "50%",
            background: connected ? "#16a34a" : "#dc2626",
            display: "inline-block",
            animation: connected ? "pulse 2s infinite" : "none",
          }} />
          {connected ? "Live" : "Connecting…"}
        </div>
        {lastEvent && (
          <span style={{ fontSize: 12, color: "#9ca3af" }}>Last event: {lastEvent}</span>
        )}
        <span style={{ fontSize: 12, color: "#9ca3af", marginLeft: "auto" }}>
          {totalItems} events (last 4 hours)
        </span>
      </div>

      {/* Pane grid */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
        gap: 12,
      }}>
        {PANES.map((pane) => (
          <Pane key={pane.id} pane={pane} items={paneItems[pane.id]} />
        ))}
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </div>
  );
}
